/**
 * C87, THE shared upload control (fleet contract §16.9, Christian 2026-11-04). Every upload surface in every app is this
 * control; a single-file input or a bespoke drop area is not conformant. Lift this folder verbatim.
 *
 * - **Drop:** a drop zone, visible before anything is dragged, that highlights on drag-over and attaches what is dropped.
 * - **Browse:** a click, or Enter or Space on the focused zone, opens the file picker (several files at once).
 * - **Paste:** pasting files while the zone, or anything inside `pasteTarget`, has focus attaches them.
 * - **Per file:** name and size, an optional caption box, a progress bar while uploading, its own error (wrong type, too
 *   large, over the limit, failed) worded as such, and Remove. Errors never collapse into one message.
 * - **EN/ES and the keyboard:** every word comes from `upload.*`; every action is a focusable control.
 *
 * Removal AFTER send is the owning surface's job (the stored list renders `onRemoveStored` with this same control's
 * Remove button); see `StoredUploads` below.
 */
import { useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Input, ProgressBar } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type { UploadItem, UploadQueue } from './useUploadQueue';

const kb = (bytes: number) => Math.max(1, Math.round(bytes / 1024));

export interface UploadControlProps {
  queue: UploadQueue;
  /** Shown inside the drop zone above the hint (e.g. "No SSNs in screenshots"). */
  warning?: ReactNode;
  /** Whether each file gets a caption box (on by default, per the ruling). */
  captions?: boolean;
  /** Disables adding and removing (e.g. while the parent sends). */
  disabled?: boolean;
  testId?: string;
}

export function UploadControl({ queue, warning, captions = true, disabled = false, testId = 'upload-control' }: UploadControlProps) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const maxMb = Math.round(queue.rules.maxBytes / (1024 * 1024));

  const browse = () => { if (!disabled) input.current?.click(); };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); browse(); }
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!over) setOver(true);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (!disabled) queue.add(Array.from(e.dataTransfer.files ?? []));
  };
  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (files.length === 0 || disabled) return;
    e.preventDefault();
    queue.add(files);
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId} onPaste={onPaste}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-label={t('upload.zoneLabel')}
        data-testid={`${testId}-zone`}
        data-over={over ? 'true' : 'false'}
        onClick={browse}
        onKeyDown={onKeyDown}
        onDragEnter={onDragOver}
        onDragOver={onDragOver}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={`rounded-md border-2 border-dashed px-4 py-3 text-sm text-center cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
          over ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'
        }`}
      >
        {warning && <p className="text-amber-800 mb-1" role="note" data-testid={`${testId}-warning`}>{warning}</p>}
        <p className="font-medium">{over ? t('upload.dropNow') : t('upload.zone')}</p>
        <p className="text-xs text-gray-600">
          {t('upload.hint', { max: queue.rules.maxFiles, mb: maxMb })}
        </p>
      </div>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        accept={queue.rules.accept.includes('*') ? undefined : queue.rules.accept.join(',')}
        data-testid={`${testId}-input`}
        onChange={e => { queue.add(Array.from(e.target.files ?? [])); e.target.value = ''; }}
      />
      {queue.items.length > 0 && (
        <ul className="flex flex-col gap-2" data-testid={`${testId}-list`}>
          {queue.items.map(item => (
            <UploadRow key={item.id} item={item} captions={captions} disabled={disabled} queue={queue} />
          ))}
        </ul>
      )}
    </div>
  );
}

function UploadRow({ item, captions, disabled, queue }: { item: UploadItem; captions: boolean; disabled: boolean; queue: UploadQueue }) {
  const { t } = useTranslation();
  const name = item.file.name;
  const mb = Math.round(queue.rules.maxBytes / (1024 * 1024));
  const errorText =
    item.error === 'type' ? t('upload.error.type', { name })
    : item.error === 'size' ? t('upload.error.size', { name, mb })
    : item.error === 'limit' ? t('upload.error.limit', { name, max: queue.rules.maxFiles })
    : item.error === 'failed' ? t('upload.error.failed', { name })
    : null;
  return (
    <li className="flex flex-col gap-1 rounded border px-2 py-1" data-testid="upload-row" data-status={item.status}>
      <div className="flex items-center gap-2 text-sm">
        <span className="break-all">{name}</span>
        <span className="text-gray-600">{t('upload.size', { kb: kb(item.file.size) })}</span>
        {item.status === 'done' && <span className="text-green-700">{t('upload.done')}</span>}
        <span className="ml-auto">
          <Button
            size="small"
            appearance="subtle"
            disabled={disabled || item.status === 'uploading'}
            aria-label={t('upload.remove', { name })}
            onClick={() => queue.remove(item.id)}
          >
            {t('upload.removeShort')}
          </Button>
        </span>
      </div>
      {captions && item.status !== 'rejected' && (
        <Input
          size="small"
          value={item.caption}
          disabled={disabled || item.status === 'uploading' || item.status === 'done'}
          placeholder={t('upload.captionPlaceholder')}
          aria-label={t('upload.caption', { name })}
          maxLength={300}
          onChange={(_, d) => queue.setCaption(item.id, d.value)}
        />
      )}
      {item.status === 'uploading' && (
        <ProgressBar value={item.progress / 100} aria-label={t('upload.progress', { name, percent: item.progress })} />
      )}
      {errorText && <p role="alert" className="text-xs text-red-700" data-testid="upload-row-error">{errorText}</p>}
    </li>
  );
}

/** A file already stored on the surface: the same Remove, within the owner's rights (C87 (i)). */
export interface StoredUpload {
  key: string;
  name: string;
  caption?: string | null;
  canRemove: boolean;
}

export function StoredUploads({ files, onRemove, removing }: {
  files: StoredUpload[];
  onRemove: (key: string) => void;
  removing?: string | null;
}) {
  const { t } = useTranslation();
  if (files.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1" data-testid="stored-uploads">
      {files.map(f => (
        <li key={f.key} className="flex items-center gap-2 text-sm">
          <span className="break-all">{f.name}</span>
          {f.caption && <span className="text-gray-600">— {f.caption}</span>}
          {f.canRemove && (
            <span className="ml-auto">
              <Button size="small" appearance="subtle" disabled={removing === f.key}
                aria-label={t('upload.remove', { name: f.name })} onClick={() => onRemove(f.key)}>
                {t('upload.removeShort')}
              </Button>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
